/**
 * #700 — EL TOTAL DE UNA COTIZACIÓN, CON LO QUE ESE NÚMERO NO ESTÁ DICIENDO.
 *
 * ## Por qué existe
 *
 * `quotations.total` se guarda SIEMPRE, incluso cuando nadie decidió el margen.
 * Lo escribe `calculate_quotation_totals(uuid)` con un `COALESCE(risk, 0)` que
 * #593 puso a propósito para que un borrador pueda mostrar el costo sin dejar
 * la fila en NULL. La decisión es correcta y no se toca.
 *
 * Lo que #700 midió es que ese número, al leerse, se ve **idéntico** al de una
 * cotización que alguien decidió emitir a costo. Dos cotizaciones gemelas con
 * la misma línea (10 × 10 000) —una con márgenes NULL, otra con 0 explícito—
 * mostraban las dos ₡113 000. Con los márgenes reales del cliente (30/40) esa
 * línea valdría 205 660: un 45% más. Un revisor de Administración parado en
 * `under_review` no tenía con qué distinguirlas.
 *
 * La señal existía —`risk_percentage` en null— pero vivía FUERA del lugar donde
 * se mira el precio, y exigía que el lector supiera de antemano que ese null
 * invalida el monto de al lado. Este componente la pone al lado del monto.
 *
 * ## Lo que NO hace
 *
 * No oculta ni cambia el número: la base lo tiene guardado y esconderlo sería
 * otra forma de mentir. Sólo lo rotula.
 *
 * Un margen 0 DELIBERADO no dispara nada: cotizar a costo es una decisión que
 * alguien tiene derecho a tomar, y tratarla como sospechosa sería volver a
 * colapsar la distinción, esta vez en la otra dirección.
 *
 * ## `marginUndefined` es un prop y NO se deduce de la fila
 *
 * Deducirlo acá adentro con `margenSinDefinir(quotation)` parece lo natural y
 * es una trampa medida: `quotationService.listQuotations` **no pide**
 * `risk_percentage` ni `profit_percentage` —#505 los saca del select a
 * propósito, porque esa lista la ve un cliente— así que sobre esas filas el
 * predicado da `true` SIEMPRE y el componente rotularía cada cotización con una
 * advertencia falsa fabricada por el propio arreglo.
 *
 * Por eso: sin el prop, el componente NO AFIRMA NADA. "No sé" y "no falta
 * margen" se renderizan igual —el número pelado— pero sólo el primero es
 * honesto cuando la fila no trae los datos. Quien tiene la fila completa pasa
 * `marginUndefined={margenSinDefinir(fila)}`.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../utils/formatters';
import { MARGEN_SIN_DEFINIR_LABEL } from '../../utils/calculations';

const QuotationTotal = ({ quotation, marginUndefined, bold = false, className = '' }) => {
  const monto = formatCurrency(quotation?.total);
  const numero = bold ? <strong>{monto}</strong> : monto;

  if (marginUndefined !== true) return <span className={className}>{numero}</span>;

  return (
    <span
      className={className}
      data-testid="quotation-total-sin-margen"
      title={MARGEN_SIN_DEFINIR_LABEL}
    >
      {numero}{' '}
      <i
        className="bi bi-exclamation-triangle-fill text-warning ms-1"
        role="img"
        aria-label={MARGEN_SIN_DEFINIR_LABEL}
      ></i>
    </span>
  );
};

QuotationTotal.propTypes = {
  quotation: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    risk_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    profit_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /**
   * `true` = medido y falta el margen. `false` = medido y está decidido.
   * Omitido = no se sabe (la fila no trae los porcentajes) y no se afirma nada.
   */
  marginUndefined: PropTypes.bool,
  bold: PropTypes.bool,
  className: PropTypes.string
};

export default QuotationTotal;
